import { getApiUrl, Icons } from "@app/shared";
import cn from "classnames";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { HoverTooltip } from "./HoverTooltip";
import { TableScrollContext } from "./TableScrollContext";

// Constant cell size keeps every row the same height regardless of state or image aspect ratio.
const SNAPSHOT_BOX = "flex h-10 w-14 items-center justify-center overflow-hidden rounded bg-background-page-highlight-gray";
const SNAPSHOT_EMPTY = "h-10 w-14";

// Thumbnails that have already loaded this session. The table keeps a couple screens of rows mounted
// off-viewport (so scrolling back is instant), but those mounted rows must NOT all fetch — only the
// ones actually on screen do. A row that already loaded renders immediately on remount (browser
// cache hit). Just url strings — cheap to keep for the session.
const loadedThumbnails = new Set<string>();

type EventSnapshotProps = {
    organizationId?: string;
    deviceId?: string;
    externalTargetId?: string;
    hasPhoto: boolean;
    onZoom?: (url: string) => void;
};

/**
 * Snapshot cell for the events tables. Distinguishes four states, all the same fixed size so rows
 * stay uniform:
 *  - no photo on record: blank cell, no icon
 *  - loading (or off screen / scrolling): neutral camera placeholder
 *  - loaded: the thumbnail (hover for a medium preview, click to open the full image)
 *  - failed to load: red camera icon with an error message on hover
 *
 * A thumbnail is fetched only when the cell is actually on screen AND scrolling has settled, so a
 * fast scroll past the many mounted-but-off-screen rows doesn't fire a fetch for each one. Once
 * loaded it stays loaded (and rows already loaded this session render straight from cache).
 */
export const EventSnapshot: FC<EventSnapshotProps> = ({ organizationId, deviceId, externalTargetId, hasPhoto, onZoom }) => {
    const isScrolling = useContext(TableScrollContext);
    const placeholderRef = useRef<HTMLDivElement>(null);
    const [onScreen, setOnScreen] = useState(false);

    const hasImage = hasPhoto && !!organizationId && !!deviceId && !!externalTargetId;
    const base = hasImage ? `/api/organization/${organizationId}/device/${deviceId}/target/${externalTargetId}` : "";
    const thumbnailUrl = hasImage ? getApiUrl(`${base}/thumbnail`) : "";
    const fullUrl = hasImage ? getApiUrl(`${base}/photo`) : "";
    const alreadyLoaded = hasImage && loadedThumbnails.has(thumbnailUrl);

    const [load, setLoad] = useState(alreadyLoaded);
    const [status, setStatus] = useState<"loading" | "loaded" | "error">(alreadyLoaded ? "loaded" : "loading");
    const [hovered, setHovered] = useState(false);

    // Track whether the not-yet-loaded cell is actually on screen. The observer only fires when a
    // cell crosses the viewport edge (a couple cells per frame), so it doesn't burden scrolling.
    useEffect(() => {
        if (!hasImage || load) return;
        const el = placeholderRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting), { rootMargin: "100px" });
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasImage, load]);

    // Fetch only on-screen cells, and only once scrolling has settled (the short delay skips brief
    // mid-scroll pauses). Rows seen earlier this session skip the wait entirely.
    useEffect(() => {
        if (load) return;
        if (alreadyLoaded) {
            setLoad(true);
            return;
        }
        if (isScrolling || !onScreen) return;
        const settle = setTimeout(() => setLoad(true), 150);
        return () => clearTimeout(settle);
    }, [load, alreadyLoaded, isScrolling, onScreen]);

    // No photo for this event at all → blank cell.
    if (!hasImage) {
        return <div className={SNAPSHOT_EMPTY} />;
    }

    // Not loading yet (off screen, or scrolling) → observed placeholder.
    if (!load) {
        return (
            <div ref={placeholderRef} className={SNAPSHOT_BOX}>
                <Icons.Photo className="text-muted-body-text" size="1.25em" />
            </div>
        );
    }

    // Failed to load → red placeholder with an error message on hover (not clickable).
    if (status === "error") {
        return (
            <HoverTooltip hoverable={<div className={SNAPSHOT_BOX}><Icons.Photo className="text-red-600" size="1.25em" /></div>} placement="right" delayMs={150}>
                <span className="text-sm text-red-600">Failed to load snapshot</span>
            </HoverTooltip>
        );
    }

    const thumbnail = (
        <div
            className={cn(SNAPSHOT_BOX, "relative cursor-pointer")}
            onMouseEnter={() => setHovered(true)}
            onClick={(e) => {
                e.stopPropagation();
                onZoom?.(fullUrl);
            }}
        >
            {status === "loading" && <Icons.Photo className="text-muted-body-text" size="1.25em" />}
            <img
                src={thumbnailUrl}
                alt="Event snapshot"
                loading="lazy"
                decoding="async"
                className={cn("absolute inset-0 h-full w-full object-cover", status !== "loaded" && "opacity-0")}
                onLoad={() => {
                    loadedThumbnails.add(thumbnailUrl);
                    setStatus("loaded");
                }}
                onError={() => setStatus("error")}
            />
        </div>
    );

    // `hoverable` is the always-visible trigger; the popup children (full-res image) only mount once
    // hovered, so the larger bytes are never requested for rows the user doesn't inspect.
    return (
        <HoverTooltip hoverable={thumbnail} placement="right" delayMs={150}>
            {hovered ? <img src={fullUrl} alt="Event snapshot enlarged" decoding="async" className="max-h-72 max-w-xs object-contain" /> : null}
        </HoverTooltip>
    );
};
